import { useMemo } from 'react';
import { StanMultiSelect } from '@/components';
import { Option } from '@/components/core-components/StanMultiSelect/types';
import { useDefaultStore } from '@/stores/defaultStore';
import { useUser } from '@/queries/useUser';
import { buildProducts, ProductFilterOption } from '@/lib/helpers';
import { useExperiments } from '@/features/Experiments/hooks';
import { EXPERIMENTS } from '@/features/Experiments/constants';

interface ProductSelectorProps {
    selectedProductIds: number[];
    onProductsChange: (productIds: number[]) => void;
    onlyPaidProducts?: boolean;
    includeAllProductsTag?: boolean;
    includeNewReferralTag?: boolean;
    label?: string;
    placeholder?: string;
    error?: boolean;
    hintText?: string;
}

export const ProductSelector = ({
    selectedProductIds,
    onProductsChange,
    onlyPaidProducts = false,
    includeAllProductsTag = true,
    includeNewReferralTag = true,
    label = 'Product',
    placeholder = 'Select products...',
    error = false,
    hintText,
}: ProductSelectorProps) => {
    const { store } = useDefaultStore();
    const { user } = useUser();
    const { hasExperiment } = useExperiments();

    // Build product list
    const products = useMemo(() => {
        if (!store || !user) return [];
        return buildProducts({
            store,
            funnels: [],
            filters: {
                paidOnly: onlyPaidProducts,
            },
            format: 'options',
            user,
        }) as ProductFilterOption[];
    }, [store, user, onlyPaidProducts]);

    // Build options including special tags
    const options = useMemo(() => {
        const opts: Option[] = [];

        if (includeAllProductsTag) {
            opts.push({ id: '0', label: 'All Products' });
        }

        const isNewReferralTagAllowed = hasExperiment(EXPERIMENTS.EMAIL_FLOW_NEW_REFERRAL_TAG);
        if (includeNewReferralTag && isNewReferralTagAllowed) {
            opts.push({ id: '-1', label: 'New Referrals' });
        }

        products.forEach(product => {
            if (product.id !== undefined && product.label) {
                opts.push({ id: String(product.id), label: product.label });
            }
        });

        return opts;
    }, [products, includeAllProductsTag, includeNewReferralTag, hasExperiment]);

    // Convert selected IDs to labels for the multiselect
    const selectedLabels = useMemo(() => {
        const labels: string[] = [];

        selectedProductIds.forEach(id => {
            const option = options.find(opt => opt.id === String(id));
            if (option) {
                labels.push(option.label);
            }
        });

        return labels;
    }, [selectedProductIds, options]);

    const handleProductsChange = (labels: string[]) => {
        const ids: number[] = [];

        labels.forEach(label => {
            const option = options.find(opt => opt.label === label);
            if (option) {
                ids.push(Number(option.id));
            }
        });

        const hasAllProducts = ids.includes(0);
        const hasOtherProducts = ids.some(id => id !== 0);

        // If "All Products" was just selected, keep only "All Products"
        if (hasAllProducts && hasOtherProducts) {
            const wasAllProductsSelected = selectedProductIds.includes(0);
            if (!wasAllProductsSelected) {
                onProductsChange([0]);
                return;
            } else {
                onProductsChange(ids.filter(id => id !== 0));
                return;
            }
        }

        onProductsChange(ids);
    };

    return (
        <div className='flex flex-col gap-2'>
            {label && <label className='text-label-xs-bold'>{label}</label>}
            <StanMultiSelect
                id='product-selector'
                name='product-selector'
                value={selectedLabels}
                options={options}
                onChange={handleProductsChange}
                placeholder={placeholder}
                allowCreate={false}
                showOptions
            />
            {error && hintText && (
                <span className='text-label-xs-regular text-negative-fg-negative-strong'>{hintText}</span>
            )}
        </div>
    );
};
